import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { CheckCircle, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Unsubscribe() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/api/waitlist/unsubscribe`, {
        email: email.toLowerCase().trim()
      });
      setSuccess(true);
    } catch (err) {
      // Show success even on error for privacy (don't reveal if email exists)
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6" data-testid="unsubscribe-success">
        <Card className="bg-slate-800 border-slate-700 p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-400" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Data Removed</h1>
          <p className="text-[#a3b8d1] mb-8">
            Your data has been removed from the SeedSynch waitlist.
          </p>
          <Link to="/">
            <Button className="btn-primary">
              <ArrowLeft size={16} className="mr-2" />
              Back to Home
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6" data-testid="unsubscribe-page">
      <Card className="bg-slate-800 border-slate-700 p-10 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-slate-700 flex items-center justify-center mx-auto mb-4">
            <Trash2 className="text-slate-400" size={24} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Remove Your Data</h1>
          <p className="text-[#a3b8d1] text-sm leading-relaxed">
            Enter the email address you used to join the SeedSynch waitlist. Your data will be permanently deleted.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              placeholder="Enter your email"
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
              disabled={loading}
              data-testid="unsubscribe-email-input"
            />
            {error && (
              <p className="text-red-400 text-xs mt-2">{error}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            data-testid="unsubscribe-submit-btn"
          >
            {loading ? 'Processing...' : 'Remove Me'}
          </Button>
        </form>

        <div className="mt-8 pt-6 border-t border-slate-700 text-center">
          <Link 
            to="/" 
            className="text-slate-400 hover:text-blue-400 text-sm transition-colors"
          >
            ← Back to SeedSynch
          </Link>
        </div>
      </Card>
    </div>
  );
}
